// Settings → General: owner WhatsApp alerts for the kitchen flow (Stage 3).
// Admin only, and only with kitchen_flow on — renders nothing otherwise, so
// live tenants see no new setting. Off by default; sending also needs the
// deploy's WhatsApp number and Meta-approved templates.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useAllSettings, useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { useToast } from '@/hooks/use-toast'
import { plainError } from '@/components/store/inventory/prep/prepRecipeDraft'
import { getStoredUserRole } from '@/lib/staff-roles'
import { AL } from '@/lib/inventoryFlow/alertStrings'
import {
  ALERT_LIMITS, ALERT_RULES, KITCHEN_TEMPLATES, alertsConfigError, alertsFieldErrors, normalisePhone, parseAlertsConfig, renderTemplatePreview, ruleOn,
  type AlertNumberField, type KitchenAlertsConfig,
} from '@/lib/inventoryFlow/alertLogic'

export function KitchenFlowAlertsSettingCard({ showToast }: { showToast?: (t: 'success' | 'error', m: string) => void }) {
  const enabled = useKitchenFlowEnabled()
  const all = useAllSettings()
  const stored = all?.['kitchen_flow.alerts']
  const qc = useQueryClient()
  const { toast } = useToast()
  // Settings → General passes no toast of its own; a save must still be seen.
  const say = (t: 'success' | 'error', m: string) =>
    showToast ? showToast(t, m) : t === 'error' ? toast({ variant: 'destructive', title: AL.saveFailed, description: m }) : toast({ title: m })
  const [c, setC] = useState<KitchenAlertsConfig>(() => parseAlertsConfig(stored))
  // Unsaved edits survive a refetch; the form follows the server only when
  // nothing is being edited.
  const [dirty, setDirty] = useState(false)
  useEffect(() => {
    if (!dirty) setC(parseAlertsConfig(stored))
  }, [stored, dirty])
  const save = useMutation({
    mutationFn: (next: KitchenAlertsConfig) => apiClient.updateSetting('kitchen_flow.alerts', next),
    onSuccess: async () => {
      say('success', AL.saved)
      // Follow the server again once it has the saved copy (never the old one).
      await qc.invalidateQueries({ queryKey: ['settings', 'all'] })
      setDirty(false)
    },
    onError: (e: Error) => say('error', plainError(e)),
  })
  if (!enabled || getStoredUserRole() !== 'admin') return null

  const err = alertsConfigError(c)
  const fieldErrors = alertsFieldErrors(c)
  const set = (patch: Partial<KitchenAlertsConfig>) => {
    setDirty(true)
    setC((x) => ({ ...x, ...patch }))
  }
  const numField = (key: AlertNumberField, label: string) => {
    const lim = ALERT_LIMITS[key]
    const fe = fieldErrors[key]
    return (
      <div className="space-y-1">
        <Label htmlFor={`kfa-${key}`}>{label}</Label>
        <Input id={`kfa-${key}`} type="number" inputMode="numeric" min={lim.min} max={lim.max} step={lim.whole ? 1 : 'any'} className="w-32"
          value={Number.isFinite(c[key]) ? String(c[key]) : ''} aria-invalid={fe != null}
          onChange={(e) => set({ [key]: e.target.value.trim() === '' ? Number.NaN : Number(e.target.value) } as Partial<KitchenAlertsConfig>)} />
        {fe && (
          <p className="text-sm text-danger-fg">
            {fe === 'missing' ? AL.numberMissing : fe === 'whole' ? AL.numberWhole : AL.numberRange(lim.min, lim.max)}
          </p>
        )}
      </div>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">{AL.title}</CardTitle>
        <CardDescription>{AL.help}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-sm text-warning-fg">{AL.metaWarning}</p>
        <div className="flex items-center gap-3">
          <Switch id="kfa-enabled" checked={c.enabled} onCheckedChange={(v) => set({ enabled: v })} />
          <Label htmlFor="kfa-enabled">{AL.enabled}</Label>
        </div>

        <div className="space-y-2">
          <p className="font-medium">{AL.recipients}</p>
          <p className="text-sm text-muted-foreground">{AL.recipientsHelp}</p>
          {c.recipients.map((r, i) => {
            const bad = r.phone.trim() !== '' && normalisePhone(r.phone) == null
            return (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Input className="w-40" placeholder={AL.label} value={r.label}
                  onChange={(e) => set({ recipients: c.recipients.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                <Input className="w-52" inputMode="tel" placeholder={AL.phone} value={r.phone} aria-invalid={bad}
                  onChange={(e) => set({ recipients: c.recipients.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)) })} />
                {bad && <span className="text-sm text-danger-fg">{AL.badPhone}</span>}
                <Button variant="ghost" onClick={() => set({ recipients: c.recipients.filter((_, j) => j !== i) })}>{AL.remove}</Button>
              </div>
            )
          })}
          {c.recipients.length < 10 && (
            <Button variant="outline" onClick={() => set({ recipients: [...c.recipients, { label: '', phone: '' }] })}>{AL.add}</Button>
          )}
        </div>

        <div className="space-y-2">
          <p className="font-medium">{AL.rules}</p>
          {ALERT_RULES.map((rule) => (
            <div key={rule} className="flex items-center gap-3">
              <Switch id={`kfa-rule-${rule}`} checked={ruleOn(c, rule)} onCheckedChange={(v) => set({ rules: { ...c.rules, [rule]: v } })} />
              <Label htmlFor={`kfa-rule-${rule}`}>{AL.ruleNames[rule]}</Label>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <p className="font-medium">{AL.thresholds}</p>
          <div className="flex flex-wrap gap-4">
            {numField('avt_pct', AL.avtPct)}
            {numField('price_jump_pct', AL.priceJumpPct)}
            {numField('expiry_days', AL.expiryDays)}
            {numField('overdue_po_days', AL.overduePoDays)}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-3">
            <Switch id="kfa-digest" checked={c.digest} onCheckedChange={(v) => set({ digest: v })} />
            <Label htmlFor="kfa-digest">{AL.digest}</Label>
          </div>
          {c.digest && numField('digest_hour', AL.digestHour)}
        </div>

        <details className="rounded-md border p-3 text-sm">
          <summary className="cursor-pointer font-medium">{AL.preview}</summary>
          <p className="mt-2 text-muted-foreground">{renderTemplatePreview(KITCHEN_TEMPLATES.alert, ['Karak & Coal', 'Stock arrived short', '2 kg Basmati missing on REQ-20260927-004 (checked by Ali)'])}</p>
          <p className="mt-2 text-muted-foreground">{renderTemplatePreview(KITCHEN_TEMPLATES.digest, ['Karak & Coal', 'Sat 26 Sep 2026',
            '6 sent, 94% of what was asked arrived, typically 1 h 10 min from asking to arriving',
            '1 short receipts, value 850; Kitchen unexplained 2400', 'worth 412000; 2 lots expiring within 3 days; 5 items below par',
            '3 price changes, paid 1800 over earlier prices; 1 supplier orders late'])}</p>
        </details>

        {err === 'need_number' && <p className="text-sm text-danger-fg">{AL.needNumber}</p>}
        {err === 'bad_number' && <p className="text-sm text-danger-fg">{AL.fixNumbers}</p>}
        <Button disabled={save.isPending || err != null} onClick={() => save.mutate({
          ...c, recipients: c.recipients.map((r) => ({ label: r.label.trim(), phone: normalisePhone(r.phone) ?? r.phone })),
        })}>{AL.save}</Button>
      </CardContent>
    </Card>
  )
}
